/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { compact } from 'lodash'

import type { UserProfile, UserProfileFromApi } from '@modules/ui-types'
import { useGetUserResourcesQuery } from '@modules/role-assignments-user-components/ProjectRoleAssignmentsSummary/lib'
import { useGetOrganizationResourcesQuery } from '@modules/role-assignments-admin-components/ProjectRoleAssignmentsSummary/lib'
import type { NormalizedResource } from '@modules/role-assignments-lib/types'
import { useConfig } from '@modules/cui/ConfigContext'

import { getConfigForKey } from '@/store'

export const shouldShowOrganization = (
  profile: UserProfile | UserProfileFromApi | null,
): boolean => {
  if (!profile) {
    return false
  }

  // Organizations are not supported by Heroku
  const isHeroku = getConfigForKey(`APP_FAMILY`) === `heroku`

  if (isHeroku) {
    return false
  }

  return true
}

export const useGetNormalizedDeploymentAndProjectList: (
  organizationId: string | undefined,
) => NormalizedResource[] = (organizationId = '') => {
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  const adminViewResources = useGetOrganizationResourcesQuery(organizationId, {
    enabled: isAdminConsole && !!organizationId,
  })

  const userViewResources = useGetUserResourcesQuery({
    enabled: !isAdminConsole,
  })

  const resourceList = isAdminConsole ? adminViewResources : userViewResources

  return compact(
    resourceList
      .filter(({ data }) => !!data)
      .flatMap((resourceGroup) => {
        const { data, resourceType } = resourceGroup
        return data?.map(({ id, name }) => ({
          resourceType,
          id,
          name,
        }))
      }),
  )
}
